import { useRef, useEffect } from 'react';
import { ViewColumnsIcon, BookmarkIcon, ChevronDownIcon, ArrowDownTrayIcon, SquaresPlusIcon } from '@heroicons/react/16/solid';
import SharedButton from '../../../../../../global/Button/Button';
import BaseTooltip from '../../../../../../global/Tooltip/BaseTooltip';
import { ColumnVisibilityModels } from '../../../../../../components/Table/ColumnVisibilityModels';
import { SaveDropdown } from './SaveDropdown';
import { chemicalRestrictedColumns, defaultModel, DOWNLOAD_EXTENSIONS } from '../../../constants';
import { GridToolbarProps } from '../types';

export function GridToolbar({
  isColumnSelectOpen,
  onToggleColumns,
  sourceOpen,
  onToggleSource,
  source,
  sourceWeights,
  showSaveDropdown,
  setShowSaveDropdown,
  onSaveComplete,
  onSaveColumnSelection,
  onSaveScore,
  isSaving,
  onResetToDefault,
  isAuthenticated,
  isAcademic,
  isTrial,
  view,
  tab,
  isEvidence,
  isMapping,
  mappingContent,
  columnVisibilityModel,
  columnOrder,
  getFilterValueRef,
  searchItems,
  onDownload,
  showEnrichmentTrigger,
  enrichmentSelectionMode,
  onToggleEnrichmentSelection,
}: GridToolbarProps) {
  const saveDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(event.target as Node)) {
        setShowSaveDropdown(false);
      }
    };

    if (showSaveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveDropdown, setShowSaveDropdown]);

  const getSaveDropdownProps = () => {
    const columnFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];
    const contextParams = ['view', 'tab', 'source', 'idents'];
    const activeFilters = columnFilters.filter(
      (f: any) =>
        !contextParams.includes(f.name) &&
        f.value &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
    );
    const hasFilters = activeFilters.length > 0;
    const hasSearchItems = searchItems && searchItems.length > 0;

    const baseDefaultModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const restrictChemical = isAuthenticated && isAcademic;
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

    const computedDefaultModel = defaultModel(baseDefaultModel, restrictedList);

    const hasCustomVisibility =
      columnVisibilityModel &&
      computedDefaultModel &&
      JSON.stringify(columnVisibilityModel) !== JSON.stringify(computedDefaultModel);

    const defaultColumnOrder = computedDefaultModel
      ? Object.keys(computedDefaultModel).filter(key => computedDefaultModel[key] === true)
      : [];

    const hasCustomOrder =
      columnOrder &&
      columnOrder.length > 0 &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

    const hasCustomColumns = hasCustomVisibility || hasCustomOrder;

    return {
      hasSearchItems,
      hasFilters,
      hasCustomColumns,
    };
  };

  const shouldShowResetButton = () => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const restrictChemical = isAuthenticated && isAcademic;
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

    const computedDefaultModel = defaultModel(baseDefaultModel, restrictedList);

    const hasCustomVisibility =
      columnVisibilityModel &&
      computedDefaultModel &&
      JSON.stringify(columnVisibilityModel) !== JSON.stringify(computedDefaultModel);

    const defaultColumnOrder = computedDefaultModel
      ? Object.keys(computedDefaultModel).filter(key => computedDefaultModel[key] === true)
      : [];

    const hasCustomOrder =
      columnOrder &&
      columnOrder.length > 0 &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

    return hasCustomVisibility || hasCustomOrder;
  };

  const saveDropdownProps = getSaveDropdownProps();

  return (
    <div className="col-6 text-right">
      <div className="row justify-content-end items-center">
        <SharedButton onClick={onToggleColumns} arrow={isColumnSelectOpen ? 'up' : 'down'} className="mt-2">
          <ViewColumnsIcon className="w-5 mr-2" />
          Select Columns
        </SharedButton>
        <SharedButton onClick={onToggleSource} arrow={sourceOpen ? 'up' : 'down'} className="mt-2">
          Source: {sourceWeights.length > 1 ? 'CUSTOM' : source.replace('_', ' ')}
        </SharedButton>
      </div>

      <div className="row justify-content-end items-center">
        {(isAuthenticated && !isAcademic && !isTrial) || showEnrichmentTrigger ? (
          <div style={{ position: 'relative' }} ref={saveDropdownRef}>
            <SharedButton
              onClick={() => setShowSaveDropdown(!showSaveDropdown)}
              disabled={isSaving && !showEnrichmentTrigger}
              className="mt-2"
            >
              {showEnrichmentTrigger ? (
                <SquaresPlusIcon className="w-5 mr-2" />
              ) : (
                <BookmarkIcon className="w-5 mr-2" />
              )}
              {showEnrichmentTrigger ? 'Actions' : 'Save'}
              <ChevronDownIcon
                className="w-4 ml-2 transition-transform duration-200"
                style={{ transform: showSaveDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </SharedButton>
            {showSaveDropdown && (
              <SaveDropdown
                {...saveDropdownProps}
                sourceWeights={sourceWeights}
                tab={tab}
                onSaveComplete={() => {
                  onSaveComplete();
                  setShowSaveDropdown(false);
                }}
                onSaveColumnSelection={() => {
                  onSaveColumnSelection();
                  setShowSaveDropdown(false);
                }}
                onSaveScore={() => {
                  onSaveScore();
                  setShowSaveDropdown(false);
                }}
                showEnrichmentTrigger={showEnrichmentTrigger}
                enrichmentSelectionMode={enrichmentSelectionMode}
                onEnrichmentAction={
                  onToggleEnrichmentSelection
                    ? () => {
                        onToggleEnrichmentSelection();
                        setShowSaveDropdown(false);
                      }
                    : undefined
                }
                showSaveOptions={isAuthenticated && !isAcademic && !isTrial}
              />
            )}
          </div>
        ) : null}

        {shouldShowResetButton() && (
          <BaseTooltip content="Reset columns to default view" position="bottom">
            <SharedButton onClick={onResetToDefault} className="mt-2" variant="secondary">
              Reset Columns
            </SharedButton>
          </BaseTooltip>
        )}
      </div>

      {/* Download Section */}
      {view !== 'ALL' && (
        <div className="row justify-content-end items-center">
          {DOWNLOAD_EXTENSIONS.map((extension, index) => (
            <BaseTooltip
              key={extension.value || index}
              content={`This will download a zip with search info a result`}
              position="bottom"
              contentClassName="max-w-xl"
              style={{ whiteSpace: 'break-spaces', width: '15em' }}
            >
              <SharedButton onClick={() => onDownload(extension)} className="mt-2">
                {extension.label}
                <ArrowDownTrayIcon className="w-4 ml-2" />
              </SharedButton>
            </BaseTooltip>
          ))}
        </div>
      )}
    </div>
  );
}
